import { memo } from "react";
import { useForm, yupResolver } from "@mantine/form";
import {
  GetPrivateSessionQuery,
  CreatePrivateSessionDurationInput,
  useGetPrivateSessionDurationLazyQuery,
  useCreatePrivateSessionDurationMutation,
  useUpdatePrivateSessionDurationMutation,
  useDeletePrivateSessionDurationMutation,
} from "src/generated/graphql";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { number, object, string } from "yup";
import Select, { ISelect } from "src/components/Select";
import { GetPrivateSession } from "src/api/graphql/queries/PrivateSession";
import { useDisclosure } from "@mantine/hooks";
import { MESSAGES } from "src/utils/constants";
import NumberInput from "src/components/NumberInput/NumberInput";

type IFormProps = Omit<CreatePrivateSessionDurationInput, "duration"> & {
  duration: string;
};

const privateSessionDurationFormSchema = object().shape({
  cost: number().required("Cost is required"),
  duration: string().required("Duration is required"),
});

type ISessionData = GetPrivateSessionQuery["privateSessionOne"];

interface IDurationDetailProps {
  sessionData: ISessionData;
  handleModal?: (id: string) => void;
  durationId?: string | undefined;
  disabled?: boolean;
  title?: string;
  visibleCards: string[];
  setvisibleCards: (e: string[]) => void;
  currentDuration?: ISelect;
  durationsData: ISelect[];
  isAllowWrite?: boolean;
}

const DurationDetail = ({
  sessionData,
  durationId,
  title,
  visibleCards,
  setvisibleCards,
  currentDuration,
  durationsData,
  isAllowWrite,
}: IDurationDetailProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(
    !!durationId
  );
  const isVisible = durationId && visibleCards.includes(durationId);
  const refetchQuery = [
    {
      query: GetPrivateSession,
      variables: { id: sessionData?.id! },
    },
  ];

  const [getDuration, { data: durationDetail }] =
    useGetPrivateSessionDurationLazyQuery({
      onCompleted() {
        getSetValues();
      },
      fetchPolicy: "network-only",
    });

  const getSetValues = () => {
    const { cost, duration, session } =
      durationDetail?.privateSessionDurationOne || {};
    let _data = {
      cost: cost!,
      duration: String(duration!),
      session: { id: session?.id! },
    };

    form.setValues(_data);
    form.resetDirty(_data);
  };

  const [createDuration, { loading: createLoading }] =
    useCreatePrivateSessionDurationMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateDuration, { loading: updateLoading }] =
    useUpdatePrivateSessionDurationMutation({
      onCompleted: (data) => {
        if (durationId) {
          toggleFormActive();
          setvisibleCards(
            visibleCards.filter(
              (e) => e !== data.updatePrivateSessionDuration.id
            )
          );
          form.resetDirty();
        } else {
          modals.closeAll();
        }
      },
      refetchQueries: refetchQuery,
    });
  const [deleteDuration, { loading: deleteLoading }] =
    useDeletePrivateSessionDurationMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IFormProps>({
    initialValues: {
      session: { id: sessionData?.id! },
      cost: Number(),
      duration: "",
    },
    validate: yupResolver(privateSessionDurationFormSchema),
  });

  const handleDelete = () => {
    deleteDuration({
      variables: { id: durationId! },
    });
  };

  const handleSubmit = async (values: IFormProps) => {
    const { duration, session, cost } = values;
    const obj = {
      session,
      cost,
      duration: Number(duration),
    };
    durationId
      ? updateDuration({
          variables: {
            data: obj,
            id: durationId,
          },
        })
      : createDuration({
          variables: {
            data: obj,
          },
        });
  };

  const handleEdit = () => {
    toggleFormActive();
    setCardVisible();
  };

  const setCardVisible = () => {
    if (!isVisible && durationId) {
      getDuration({
        variables: { id: durationId },
      });
      setvisibleCards([...visibleCards, durationId]);
    }
  };

  const handleClick = () => {
    if (isVisible) {
      if (!isFormActive) {
        getSetValues();
        toggleFormActive();
      }
      setvisibleCards(visibleCards.filter((e) => e !== durationId));
    } else {
      setCardVisible();
    }
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  return (
    <>
      <FormCard
        title={title || ""}
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        loading={updateLoading || deleteLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onCancel={handleClick}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        hideCard={!durationId}
        mb={10}
        size="md"
        radius={4}
        isFormActive={isFormActive}
        overflow="visible"
      >
        {(isVisible || !durationId) && (
          <>
            <Select
              withAsterisk
              data={
                currentDuration
                  ? [...durationsData, currentDuration]
                  : durationsData
              }
              disabled={isFormActive}
              label="Duration"
              {...form.getInputProps("duration")}
              styles={{ root: { width: "100%" } }}
            />

            <NumberInput
              withAsterisk
              label="Cost"
              maxLength={7}
              precision={2}
              {...form.getInputProps("cost")}
              disabled={isFormActive}
            />

            {!durationId && (
              <Group position="apart" mt="md" style={{ marginTop: 30 }}>
                <Button
                  radius="md"
                  variant="outline"
                  onClick={() => modals.closeAll()}
                  style={{
                    flex: 1,
                    color: theme.colors.secondaryTextColor[6],
                  }}
                  sx={{
                    backgroundColor: theme.colors.secondary[6],
                    "&:hover": {
                      background: theme.colors.secondary[5],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  radius="md"
                  type="submit"
                  disabled={createLoading || updateLoading}
                  style={{
                    flex: 1,
                  }}
                  sx={{
                    color: theme.colors.primaryTextColor[6],
                    "&:hover": {
                      backgroundColor: theme.colors.primary[4],
                    },
                  }}
                  loading={createLoading || updateLoading}
                >
                  {durationId ? "Save" : "Create"}
                </Button>
              </Group>
            )}
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(DurationDetail);
